import { useEffect, useRef, useState, Suspense } from 'react';
import { getPostBySlug, getAdjacent, getRelatedPosts } from '../blogs/index.js';
import { imageProps } from '../utils/imageProps.js';
import useDocumentMeta from '../hooks/useDocumentMeta.js';
import { SITE_URL } from '../utils/seo.js';
import NotFound from './NotFound.jsx';
import SmartImage from './SmartImage.jsx';
import { submitToWeb3Forms } from '../utils/web3forms.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toIsoDate = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

export default function BlogDetail({ slug }) {
  const articleRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [ctaValues, setCtaValues] = useState({ name: '', email: '', message: '' });
  const [ctaErrors, setCtaErrors] = useState({});
  const [ctaSent, setCtaSent] = useState(false);
  const [ctaSubmitting, setCtaSubmitting] = useState(false);
  const [ctaSubmitError, setCtaSubmitError] = useState('');

  const handleCtaChange = (key) => (e) => {
    const value = e.target.value;
    setCtaValues((prev) => ({ ...prev, [key]: value }));
    if (ctaErrors[key]) {
      setCtaErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleCtaSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!ctaValues.name.trim()) errs.name = 'Please enter your name.';
    if (!ctaValues.email.trim()) errs.email = 'Email is required.';
    else if (!EMAIL_RE.test(ctaValues.email.trim())) errs.email = 'Enter a valid email address.';
    setCtaErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setCtaSubmitError('');
    setCtaSubmitting(true);
    try {
      await submitToWeb3Forms({
        formName: 'Blog CTA',
        subject: `Blog inquiry: ${ctaValues.name}`,
        replyTo: ctaValues.email,
        fields: {
          Name: ctaValues.name,
          Email: ctaValues.email,
          Message: ctaValues.message,
          'Blog post': typeof window !== 'undefined' ? window.location.pathname : '',
        },
      });
      setCtaValues({ name: '', email: '', message: '' });
      setCtaSent(true);
      setTimeout(() => setCtaSent(false), 8000);
    } catch (err) {
      setCtaSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setCtaSubmitting(false);
    }
  };

  const post = getPostBySlug(slug);

  const publishedIso = post ? toIsoDate(post.date) : undefined;
  const modifiedIso = post ? toIsoDate(post.dateModified || post.date) : undefined;
  const hasUpdated = !!(post && post.dateModified && post.dateModified !== post.date);

  const postJsonLd = post && {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.metaTitle || post.title,
    description: post.metaDescription || post.excerpt,
    datePublished: publishedIso || post.date,
    dateModified: modifiedIso || publishedIso || post.date,
    inLanguage: 'en-US',
    articleSection: post.category,
    author: {
      '@type': 'Person',
      name: post.author || 'Eric Williams',
      jobTitle: post.authorRole || 'Product Manager',
      worksFor: { '@id': `${SITE_URL}/#organization` }
    },
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
    image: post.image || undefined,
    keywords: post.category
  };

  const articleMeta = post
    ? {
        publishedTime: publishedIso,
        modifiedTime: modifiedIso,
        author: post.author || 'Eric Williams',
        section: post.category,
        tags: Array.isArray(post.tags) ? post.tags : (post.category ? [post.category] : [])
      }
    : undefined;

  const faqJsonLd = post && post.faqs && post.faqs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a.replace(/<[^>]+>/g, '').trim()
          }
        }))
      }
    : null;

  const breadcrumbJsonLd = post && {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE_URL}/blog/${post.slug}` }
    ]
  };

  const jsonLd = post
    ? [postJsonLd, breadcrumbJsonLd, ...(faqJsonLd ? [faqJsonLd] : [])]
    : null;

  useDocumentMeta({
    title: post ? (post.metaTitle || post.title) : 'Article not found',
    description: post ? (post.metaDescription || post.excerpt) : "The article you're looking for doesn't exist.",
    path: post ? `/blog/${post.slug}` : '/blog',
    noindex: !post,
    type: 'article',
    image: post && post.image ? post.image : undefined,
    jsonLd,
    article: articleMeta
  });

  useEffect(() => {
    if (!post || !articleRef.current) return;

    const buildToc = () => {
      const el = articleRef.current;
      if (!el) return false;
      const headings = el.querySelectorAll('h2');
      if (headings.length === 0) return false;

      const items = Array.from(headings).map((h, i) => {
        const text = (h.textContent || '').trim();
        const id =
          `h-${i}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` || `h-${i}`;
        h.id = id;
        return { id, text };
      });
      setToc(items);

      // Inject mid-article CTA before the H2 nearest the midpoint
      const existing = el.querySelector('.blog-inline-cta');
      if (existing) existing.remove();
      if (headings.length >= 3) {
        const midIdx = Math.floor(headings.length / 2);
        const target = headings[midIdx];
        const cta = document.createElement('aside');
        cta.className = 'blog-inline-cta';
        cta.innerHTML = `
          <div class="blog-inline-cta__body">
            <span class="blog-inline-cta__eyebrow">Account Editor for Shopify</span>
            <p class="blog-inline-cta__text">Let customers edit their own orders in one click. Cut edit tickets by up to 92%.</p>
          </div>
          <a class="blog-inline-cta__btn" href="https://apps.shopify.com/account-editor?utm_source=website&utm_medium=blog&utm_campaign=marketing" target="_blank" rel="noopener noreferrer">Install Free<i class="ph ph-arrow-right" aria-hidden="true"></i></a>
        `;
        target.parentNode.insertBefore(cta, target);
      }
      return true;
    };

    if (buildToc()) return;

    const observer = new MutationObserver(() => {
      if (buildToc()) observer.disconnect();
    });
    observer.observe(articleRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [post]);

  useEffect(() => {
    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const total = article.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const pct = total > 0 ? (scrolled / total) * 100 : 0;
      setProgress(pct);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [post]);

  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const top = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          setActiveId(top.target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );
    toc.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const shareUrl = post ? `${SITE_URL}/blog/${post.slug}` : '';
  const shareTitle = post ? post.title : '';
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`;

  if (!post) return <NotFound />;

  const { prev, next } = getAdjacent(slug);
  const related = getRelatedPosts(slug, 3);
  const Content = post.Content;

  return (
    <section className="blog-detail">
      <div className="blog-detail__progress" aria-hidden="true">
        <div className="blog-detail__progress-bar" style={{ width: `${progress}%` }} />
      </div>
      <div className="container">
        <header className="blog-detail__hero">
          <a href="/blog" className="blog-detail__back">
            <i className="ph ph-arrow-left" aria-hidden="true"></i>
            All articles
          </a>
          <div className="blog-detail__hero-grid">
            <div className="blog-detail__hero-body">
              <span className="blog-index__tag">{post.category}</span>
              <h1 className="blog-detail__title">{post.title}</h1>
              {post.excerpt && <p className="blog-detail__excerpt">{post.excerpt}</p>}
              <div className="blog-detail__meta">
                <span className="blog-detail__meta-item">
                  <i className="ph ph-pencil-simple-line" aria-hidden="true"></i>
                  <span>
                    <strong>{post.author}</strong>
                    {post.authorRole ? `, ${post.authorRole}` : ''}
                  </span>
                </span>
                <span className="blog-detail__meta-item">
                  <i className="ph ph-calendar-dots" aria-hidden="true"></i>
                  <span>{post.date}</span>
                </span>
                {hasUpdated && (
                  <span className="blog-detail__meta-item">
                    <i className="ph ph-arrow-clockwise" aria-hidden="true"></i>
                    <span className="blog-detail__meta-updated">Updated {post.dateModified}</span>
                  </span>
                )}
                {post.readTime && (
                  <span className="blog-detail__meta-item">
                    <i className="ph ph-clock" aria-hidden="true"></i>
                    <span>{post.readTime}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="blog-detail__hero-media">
              <SmartImage
                {...imageProps(post.image, `https://placehold.co/1280x640/cccccc/171717?text=${encodeURIComponent(post.title)}`)}
                alt={post.imageAlt || post.title}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1280"
                height="640"
              />
            </div>
          </div>
        </header>

        <div className="blog-detail__layout">
          <aside className="blog-detail__toc" aria-label="On this page">
            <span className="blog-detail__toc-title">On this page</span>
            {/* Built from the article's headings after mount: the prerender
                ships it empty to match the first client render. */}
            <ol data-prerender-empty>
              {toc.map((t) => (
                <li
                  key={t.id}
                  className={activeId === t.id ? 'blog-detail__toc-item--active' : ''}
                >
                  <a href={`#${t.id}`}>{t.text}</a>
                </li>
              ))}
            </ol>

            <div className="blog-detail__share" aria-label="Share article">
              <span className="blog-detail__share-label">Share</span>
              <div className="blog-detail__share-actions">
                <a
                  href={twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-detail__share-btn"
                  aria-label="Share on Twitter"
                >
                  <i className="ph ph-x-logo" aria-hidden="true"></i>
                </a>
                <a
                  href={linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-detail__share-btn"
                  aria-label="Share on LinkedIn"
                >
                  <i className="ph ph-linkedin-logo" aria-hidden="true"></i>
                </a>
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-detail__share-btn"
                  aria-label="Share on Facebook"
                >
                  <i className="ph ph-facebook-logo" aria-hidden="true"></i>
                </a>
                <a
                  href={emailUrl}
                  className="blog-detail__share-btn"
                  aria-label="Share via Email"
                >
                  <i className="ph ph-envelope-simple" aria-hidden="true"></i>
                </a>
                <button
                  type="button"
                  className="blog-detail__share-btn"
                  onClick={handleCopy}
                  aria-label="Copy link"
                >
                  {copied ? (
                    <i className="ph ph-check" aria-hidden="true"></i>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 256 256"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M137.54,186.36a8,8,0,0,1,0,11.31l-9.94,10A56,56,0,0,1,48.38,128.4L72.5,104.28A56,56,0,0,1,149.31,102a8,8,0,1,1-10.64,12,40,40,0,0,0-54.85,1.63L59.7,139.72a40,40,0,0,0,56.58,56.58l9.94-9.94A8,8,0,0,1,137.54,186.36Zm70.08-138a56.08,56.08,0,0,0-79.22,0l-9.94,9.95a8,8,0,0,0,11.32,11.31l9.94-9.94a40,40,0,0,1,56.58,56.58L172.18,140.4A40,40,0,0,1,117.33,142,8,8,0,1,0,106.69,154a56,56,0,0,0,76.81-2.26l24.12-24.12A56.08,56.08,0,0,0,207.62,48.38Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </aside>

          <div className="blog-detail__main">
            {Array.isArray(post.takeaways) && post.takeaways.length > 0 && (
              <aside className="blog-detail__takeaways" aria-label="Key takeaways">
                <span className="blog-detail__takeaways-label">Key takeaways</span>
                <ul className="blog-detail__takeaways-list">
                  {post.takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </aside>
            )}
            <article className="blog-detail__article" ref={articleRef} data-suspense>
              <Suspense
                fallback={
                  <div className="blog-detail__loading skeleton-stack" aria-hidden="true">
                    <span className="skeleton-block skeleton-block--text" />
                    <span className="skeleton-block skeleton-block--text" />
                    <span className="skeleton-block skeleton-block--text is-short" />
                    <span className="skeleton-block skeleton-block--media" />
                    <span className="skeleton-block skeleton-block--text" />
                    <span className="skeleton-block skeleton-block--text is-short" />
                  </div>
                }
              >
                <Content />
              </Suspense>
            </article>

            {post.faqs && post.faqs.length > 0 && (
              <section className="blog-detail__faqs" aria-label="Frequently asked questions">
                <h2 className="blog-detail__faqs-title">Frequently asked questions</h2>
                <ul className="faq__list">
                  {post.faqs.map((f, idx) => {
                    const isOpen = openFaq === idx;
                    const num = String(idx + 1).padStart(2, '0');
                    return (
                      <li
                        key={idx}
                        className={`faq-item${isOpen ? ' is-open' : ''}`}
                      >
                        <div className="faq-item__trigger">
                          <span className="faq-item__num">{num}</span>
                          <span className="faq-item__question">{f.q}</span>
                          <span className="faq-item__icon" aria-hidden="true">
                            <i className="ph ph-plus" aria-hidden="true"></i>
                            <i className="ph ph-minus" aria-hidden="true"></i>
                          </span>
                        </div>
                        <div
                          className="faq-item__body"
                          dangerouslySetInnerHTML={{ __html: f.a }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>

          <aside className="blog-detail__cta-form" aria-label="Get in touch">
            <span className="blog-detail__cta-eyebrow">Get in touch</span>
            <p className="blog-detail__cta-lede">
              Questions about Account Editor? Send us a quick note and we'll reply within one business day.
            </p>
            <form className="modal__form" onSubmit={handleCtaSubmit} noValidate>
              <div className={`modal__field${ctaErrors.name ? ' modal__field--error' : ''}`}>
                <label htmlFor="ctaName">Name</label>
                <input
                  type="text"
                  id="ctaName"
                  name="name"
                  placeholder="Your name"
                  value={ctaValues.name}
                  onChange={handleCtaChange('name')}
                  aria-invalid={!!ctaErrors.name}
                />
                {ctaErrors.name && (
                  <span className="modal__field-error" role="alert">
                    <i className="ph ph-x-circle" aria-hidden="true"></i>
                    {ctaErrors.name}
                  </span>
                )}
              </div>
              <div className={`modal__field${ctaErrors.email ? ' modal__field--error' : ''}`}>
                <label htmlFor="ctaEmail">Email</label>
                <input
                  type="email"
                  id="ctaEmail"
                  name="email"
                  placeholder="you@store.com"
                  value={ctaValues.email}
                  onChange={handleCtaChange('email')}
                  aria-invalid={!!ctaErrors.email}
                />
                {ctaErrors.email && (
                  <span className="modal__field-error" role="alert">
                    <i className="ph ph-x-circle" aria-hidden="true"></i>
                    {ctaErrors.email}
                  </span>
                )}
              </div>
              <div className="modal__field">
                <label htmlFor="ctaMessage">Message</label>
                <textarea
                  id="ctaMessage"
                  name="message"
                  placeholder="How can we help?"
                  rows={4}
                  value={ctaValues.message}
                  onChange={handleCtaChange('message')}
                />
              </div>
              <button
                type="submit"
                className="btn btn--primary modal__submit"
                disabled={ctaSubmitting}
              >
                {ctaSubmitting ? 'Sending…' : 'Send message'}
                {!ctaSubmitting && <i className="ph ph-arrow-right" aria-hidden="true"></i>}
              </button>
              {ctaSent && (
                <p className="blog-detail__cta-success" role="status">
                  <i className="ph ph-check-circle" aria-hidden="true"></i>
                  Thanks — we'll be in touch shortly.
                </p>
              )}
              {ctaSubmitError && (
                <p className="blog-detail__cta-success" role="alert" style={{ color: '#c53030' }}>
                  <i className="ph ph-x-circle" aria-hidden="true"></i>
                  {ctaSubmitError}
                </p>
              )}
            </form>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="blog-detail__related" aria-label="Related articles">
            <div className="blog-detail__related-head">
              <span className="eyebrow">Keep reading</span>
              <h2 className="blog-detail__related-title">Related articles</h2>
            </div>
            <div className="blog-detail__related-grid">
              {related.map((r) => (
                <a
                  key={r.slug}
                  href={`/blog/${r.slug}`}
                  className="blog-card-v2"
                >
                  <div className="blog-card-v2__media">
                    <SmartImage
                      {...imageProps(r.image, `https://placehold.co/600x400/cccccc/171717?text=${encodeURIComponent(r.title)}`)}
                      alt={r.imageAlt || r.title}
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="400"
                    />
                  </div>
                  <div className="blog-card-v2__body">
                    <span className="blog-index__tag">{r.category}</span>
                    <h3 className="blog-card-v2__title">{r.title}</h3>
                    {r.excerpt && <p className="blog-card-v2__excerpt">{r.excerpt}</p>}
                    <div className="blog-index__meta">
                      <span>{r.date}</span>
                      {r.readTime && (
                        <>
                          <span>·</span>
                          <span>{r.readTime}</span>
                        </>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className="blog-detail__explore" aria-label="Explore Account Editor">
          <div className="blog-detail__explore-head">
            <span className="eyebrow">Explore Account Editor</span>
            <h2 className="blog-detail__explore-title">Put this into practice.</h2>
          </div>
          <div className="blog-detail__explore-links">
            <a href="/features" className="blog-detail__explore-link">
              <i className="ph ph-squares-four" aria-hidden="true"></i>
              <span className="blog-detail__explore-text">
                <strong>Features</strong>
                Self-serve edits, cancel deflection, and upsells
              </span>
              <i className="ph ph-arrow-right blog-detail__explore-arrow" aria-hidden="true"></i>
            </a>
            <a href="/pricing" className="blog-detail__explore-link">
              <i className="ph ph-tag" aria-hidden="true"></i>
              <span className="blog-detail__explore-text">
                <strong>Pricing</strong>
                Free to install, flat rate as you scale
              </span>
              <i className="ph ph-arrow-right blog-detail__explore-arrow" aria-hidden="true"></i>
            </a>
            <a href="/case-studies" className="blog-detail__explore-link">
              <i className="ph ph-chart-line-up" aria-hidden="true"></i>
              <span className="blog-detail__explore-text">
                <strong>Case studies</strong>
                Real Shopify brands, real numbers
              </span>
              <i className="ph ph-arrow-right blog-detail__explore-arrow" aria-hidden="true"></i>
            </a>
          </div>
        </section>

        <nav className="blog-detail__nav" aria-label="Article navigation">
          {prev ? (
            <a href={`/blog/${prev.slug}`} className="blog-detail__nav-link">
              <span className="blog-detail__nav-dir">
                <i className="ph ph-arrow-left" aria-hidden="true"></i>
                Previous
              </span>
              <span className="blog-detail__nav-title">{prev.title}</span>
            </a>
          ) : (
            <span />
          )}
          {next ? (
            <a href={`/blog/${next.slug}`} className="blog-detail__nav-link blog-detail__nav-link--right">
              <span className="blog-detail__nav-dir">
                Next
                <i className="ph ph-arrow-right" aria-hidden="true"></i>
              </span>
              <span className="blog-detail__nav-title">{next.title}</span>
            </a>
          ) : (
            <span />
          )}
        </nav>
      </div>
    </section>
  );
}
